import React, { useMemo } from 'react'
import { useLazyLoadQuery, graphql } from 'react-relay'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Label, Legend } from 'recharts'
import { EmptyState } from '../../../../components/EmptyState/EmptyState'
import { aiDateRanges } from '../../../../pages/Insights'
import { useGlobalState } from '../../../../state/v2/store'
import { useInsightsFilterMenuParams } from '../FilterMenu/useFilterMenuParams'
import { LabelTypeLiftGraphQuery } from './__generated__/LabelTypeLiftGraphQuery.graphql'

interface LabelTypeLiftItem {
  labelType: string
  lift: number | null
}

export const LabelTypeLiftGraph = () => {
  const [{ insightKpi, aiDateRange }] = useInsightsFilterMenuParams()
  const brandId = useGlobalState((b) => b.brand?.map((brand) => brand.id).join(','))

  const variables = useMemo<LabelTypeLiftGraphQuery['variables']>(() => ({
    skip: !brandId,
    input: {
      brandId: brandId!,
      kpi: insightKpi as any,
      aiDateRange: aiDateRange || aiDateRanges[0].value,
      evaluationType: 'TOP_PERFORMING',
    },
  }), [aiDateRange, insightKpi, brandId])

  const data = useLazyLoadQuery<LabelTypeLiftGraphQuery>(
    graphql`
      query LabelTypeLiftGraphQuery($input: GetAISummaryRequest!, $skip: Boolean!) {
        getLabelLiftByType(input: $input) @skip(if: $skip) {
          labelType
          lift
        }
      }
    `,
    variables,
    { fetchPolicy: 'network-only' }
  )

  const chartData: { labelType: string; lift: number }[] = useMemo(() => {
    const items = data.getLabelLiftByType || []
    return items.map((it) => ({ labelType: it.labelType.replace(/_/g, ' '), lift: Math.round((it.lift ?? 0) * 100) }))
  }, [data])

  if (!chartData || chartData.length === 0) {
    return <EmptyState message="No label lift data available for your filters." />
  }

  return (
    <div>
      <h5 className="text-2xl">Label Lift by Label Type</h5>
      <p className="mb-4">Shows average label lift (%) grouped by label type for selected filters.</p>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={chartData} margin={{ top: 20, right: 24, left: 24, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="labelType" tick={{ fontSize: 12 }} />
          <YAxis tickFormatter={(val: number) => `${val}%`} tick={{ fontSize: 12 }}>
            <Label value="Label Lift (%)" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
          </YAxis>
          <Tooltip formatter={(value: any, name: string) => [`${value}%`, 'Label Lift']} />
          <Legend />
          <Bar dataKey="lift" fill="#0B6D5E" name="Label Lift" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default LabelTypeLiftGraph
